import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, Mail, Search, RefreshCw, ChevronLeft, ChevronRight, ArrowUpRight, X } from '../../icons/wp-icons';
import AudioPlayer from '../Common/AudioPlayer';
import { kiZentraleApi } from '../../api/kiZentraleApi';
import { voiceAssistantApi } from '../../api/voiceAssistantApi';
import { whatsappApi } from '../../api/whatsappApi';
import { emailAgentApi } from '../../api/emailAgentApi';
import { useEmbed } from '../../hooks/useEmbed';
import { censorName, censorEmail, censorSubject, censorBody, censorPhone, censorContact } from '../../utils/demoCensor';

// HTML aus E-Mail-Inhalten entfernen
function stripHtmlTags(text) {
  if (!text) return '';
  if (!text.includes('<')) return text;
  const doc = new DOMParser().parseFromString(text, 'text/html');
  return doc.body.textContent || '';
}

const CHANNEL_FILTERS = [
  { value: 'alle', label: 'Alle Kanäle', icon: null },
  { value: 'telefon', label: 'Telefon', icon: Phone, color: 'text-purple-600', bg: 'bg-purple-100' },
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, color: 'text-green-600', bg: 'bg-green-100' },
  { value: 'email', label: 'E-Mail', icon: Mail, color: 'text-blue-600', bg: 'bg-blue-100' },
];

const TYPE_CONFIG = {
  call: { icon: Phone, color: 'text-purple-600', bg: 'bg-purple-100', badge: 'bg-purple-100 text-purple-700', label: 'Anruf' },
  whatsapp: { icon: MessageCircle, color: 'text-green-600', bg: 'bg-green-100', badge: 'bg-green-100 text-green-700', label: 'WhatsApp' },
  email: { icon: Mail, color: 'text-blue-600', bg: 'bg-blue-100', badge: 'bg-blue-100 text-blue-700', label: 'E-Mail' },
};

export default function KiProtokoll() {
  const { isEmbed } = useEmbed();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totals, setTotals] = useState({});
  const [page, setPage] = useState(1);
  const [kanal, setKanal] = useState('alle');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);

  // Detail view state
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    loadTimeline();
  }, [page, kanal, search]);

  const loadTimeline = async () => {
    setLoading(true);
    try {
      const data = await kiZentraleApi.getTimeline({ page, limit: 20, kanal, search });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 0);
      setTotals(data.totals || {});
    } catch (err) {
      console.error('Timeline laden fehlgeschlagen:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const handleItemClick = async (item) => {
    if (selectedItem?.id === item.id && selectedItem?.type === item.type) {
      setSelectedItem(null);
      setDetailData(null);
      return;
    }

    setSelectedItem(item);
    setDetailLoading(true);
    setDetailData(null);

    try {
      if (item.type === 'call') {
        const data = await voiceAssistantApi.getCallLog(item.id);
        setDetailData({ type: 'call', ...data });
      } else if (item.type === 'whatsapp') {
        const data = await whatsappApi.getConversation(item.id);
        setDetailData({ type: 'whatsapp', ...data });
      } else if (item.type === 'email') {
        const data = await emailAgentApi.getProcessedEmail(item.id);
        setDetailData({ type: 'email', ...data });
      }
    } catch (err) {
      console.error('Detail laden fehlgeschlagen:', err);
      setDetailData(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffHours < 1) {
      const diffMin = Math.floor(diffMs / (1000 * 60));
      return `vor ${diffMin} Min.`;
    }
    if (diffHours < 24) {
      return `vor ${diffHours} Std.`;
    }
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-4">
      {/* Filter Bar */}
      <div className="bg-surface rounded-card shadow-card p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Channel Filter */}
          <div className="flex gap-1 overflow-x-auto">
            {CHANNEL_FILTERS.map((filter) => (
              <button
                key={filter.value}
                onClick={() => { setKanal(filter.value); setPage(1); setSelectedItem(null); setDetailData(null); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition ${
                  kanal === filter.value
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {filter.icon && <filter.icon className="w-3.5 h-3.5" />}
                {filter.label}
                {filter.value !== 'alle' && totals[filter.value] !== undefined && (
                  <span className={`text-xs ml-1 ${kanal === filter.value ? 'text-white/80' : 'text-gray-400'}`}>
                    ({totals[filter.value]})
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
              <input
                type="text"
                placeholder="Suche nach Name, Nummer, Betreff..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-border rounded-lg text-sm"
              />
            </div>
            <button type="submit" className="px-3 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90">
              Suchen
            </button>
          </form>
        </div>
      </div>

      {/* Timeline Items */}
      <div className="bg-surface rounded-card shadow-card">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-text-muted">Keine Einträge gefunden</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {items.map((item, index) => {
              const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.email;
              const Icon = config.icon;
              const isSelected = selectedItem?.id === item.id && selectedItem?.type === item.type;

              return (
                <div key={`${item.type}-${item.id}-${index}`}>
                  <div
                    className={`p-4 transition cursor-pointer ${isSelected ? 'bg-primary/5' : 'hover:bg-gray-50'}`}
                    onClick={() => handleItemClick(item)}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className={`p-2 rounded-full ${config.bg} flex-shrink-0`}>
                        <Icon className={`w-4 h-4 ${config.color}`} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-text truncate">{censorContact(item.contact, isEmbed)}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${config.badge}`}>
                            {config.label}
                          </span>
                          {item.direction === 'outbound' && (
                            <ArrowUpRight className="w-3.5 h-3.5 text-text-muted flex-shrink-0" />
                          )}
                        </div>

                        {item.type === 'call' && (
                          <p className="text-sm text-text-muted truncate">
                            {censorBody(item.summary, isEmbed) || (item.hasTranscript ? 'Transkript vorhanden' : 'Kein Transkript')}
                            {item.duration ? ` (${Math.floor(item.duration / 60)}:${String(item.duration % 60).padStart(2, '0')})` : ''}
                          </p>
                        )}
                        {item.type === 'whatsapp' && (
                          <p className="text-sm text-text-muted truncate">
                            {censorBody(item.lastMessage, isEmbed) || censorPhone(item.contactPhone, isEmbed)}
                          </p>
                        )}
                        {item.type === 'email' && (
                          <p className="text-sm text-text-muted truncate">
                            {censorSubject(item.subject, isEmbed)}
                            {item.intent && <span className="ml-2 text-xs text-text-muted">({item.intent})</span>}
                          </p>
                        )}
                      </div>

                      {/* Time */}
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs text-text-muted">{formatTime(item.timestamp)}</p>
                        {item.status && (
                          <span className={`text-xs ${
                            item.status === 'completed' || item.status === 'aktiv' ? 'text-green-600' :
                            item.status === 'failed' ? 'text-red-600' : 'text-gray-500'
                          }`}>
                            {item.status}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Detail */}
                  {isSelected && (
                    <div className="px-4 pb-4">
                      <div className="p-4 rounded-lg border border-primary/20 bg-white">
                        {detailLoading ? (
                          <div className="flex justify-center py-4">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                          </div>
                        ) : detailData ? (
                          <>
                            {/* Call Detail */}
                            {detailData.type === 'call' && (
                              <div className="space-y-3">
                                {(detailData.recordingUrl || item.recordingUrl) && (
                                  <div>
                                    <p className="text-xs text-text-muted font-semibold mb-1 uppercase">Aufzeichnung</p>
                                    <AudioPlayer src={detailData.recordingUrl || item.recordingUrl} />
                                  </div>
                                )}
                                {(detailData.summary) && (
                                  <div>
                                    <p className="text-xs text-text-muted font-semibold mb-1 uppercase">Zusammenfassung</p>
                                    <p className="text-sm">{censorBody(detailData.summary, isEmbed)}</p>
                                  </div>
                                )}
                                {detailData.transcript && (
                                  <div>
                                    <p className="text-xs text-text-muted font-semibold mb-1 uppercase">Transkript</p>
                                    <div className="max-h-48 overflow-y-auto rounded-lg bg-gray-50 p-3">
                                      {isEmbed ? (
                                        <p className="text-sm whitespace-pre-wrap">Transkript (für Demo zensiert)</p>
                                      ) : typeof detailData.transcript === 'string' ? (
                                        <p className="text-sm whitespace-pre-wrap">{detailData.transcript}</p>
                                      ) : Array.isArray(detailData.transcript) ? (
                                        <div className="space-y-1">
                                          {detailData.transcript.map((entry, idx) => (
                                            <div key={idx} className={`text-sm ${entry.role === 'assistant' || entry.speaker === 'assistant' ? 'text-primary' : 'text-text'}`}>
                                              <span className="font-medium text-xs uppercase">{entry.role === 'assistant' || entry.speaker === 'assistant' ? 'KI' : 'Anrufer'}:</span>{' '}
                                              {entry.content || entry.text || entry.message}
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(detailData.transcript, null, 2)}</pre>
                                      )}
                                    </div>
                                  </div>
                                )}
                                {detailData.extractedData && Object.keys(detailData.extractedData).length > 0 && (
                                  <div>
                                    <p className="text-xs text-text-muted font-semibold mb-1 uppercase">Extrahierte Daten</p>
                                    <div className="grid grid-cols-2 gap-1 text-sm">
                                      {Object.entries(detailData.extractedData).map(([key, val]) => (
                                        <div key={key}><span className="text-text-muted">{key}:</span> {String(val)}</div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {!detailData.summary && !detailData.transcript && (
                                  <p className="text-sm text-text-muted">Keine Details verfügbar.</p>
                                )}
                              </div>
                            )}

                            {/* WhatsApp Detail */}
                            {detailData.type === 'whatsapp' && (
                              <div className="space-y-3">
                                <p className="text-xs text-text-muted font-semibold uppercase">Chat-Verlauf</p>
                                <div className="max-h-64 overflow-y-auto space-y-2">
                                  {(detailData.nachrichten || detailData.messages || []).length === 0 ? (
                                    <p className="text-sm text-text-muted">Keine Nachrichten</p>
                                  ) : (
                                    (detailData.nachrichten || detailData.messages || []).map((msg, idx) => {
                                      const isIncoming = msg.richtung === 'eingehend' || msg.direction === 'inbound';
                                      return (
                                        <div key={msg.id || idx} className={`flex ${isIncoming ? 'justify-start' : 'justify-end'}`}>
                                          <div className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${isIncoming ? 'bg-gray-100 text-text' : 'bg-green-500 text-white'}`}>
                                            <p className="whitespace-pre-wrap break-words">{censorBody(msg.inhalt || msg.content || msg.body, isEmbed)}</p>
                                            <p className={`text-xs mt-1 ${isIncoming ? 'text-text-muted' : 'text-green-100'}`}>
                                              {new Date(msg.erstelltAm || msg.createdAt || msg.timestamp).toLocaleString('de-DE', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
                                            </p>
                                          </div>
                                        </div>
                                      );
                                    })
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Email Detail */}
                            {detailData.type === 'email' && (
                              <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                  <div>
                                    <span className="text-text-muted">Von:</span>{' '}
                                    {detailData.kundeId && !isEmbed ? (
                                      <Link
                                        to={`/kunden/${detailData.kundeId}`}
                                        className="text-primary hover:underline inline-flex items-center gap-1"
                                        title="Zum Kunden"
                                      >
                                        {censorEmail(detailData.absender || detailData.from, isEmbed)}
                                        <ArrowUpRight className="w-3 h-3" />
                                      </Link>
                                    ) : (
                                      censorEmail(detailData.absender || detailData.from, isEmbed)
                                    )}
                                  </div>
                                  <div><span className="text-text-muted">Datum:</span> {new Date(detailData.erstelltAm || detailData.createdAt).toLocaleString('de-DE')}</div>
                                  {detailData.intent && (
                                    <div><span className="text-text-muted">Intent:</span> <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-xs">{detailData.intent}</span></div>
                                  )}
                                </div>

                                <div>
                                  <p className="text-xs text-text-muted font-semibold mb-1 uppercase">Originalinhalt</p>
                                  <div className="p-3 rounded-lg bg-gray-50 text-sm whitespace-pre-wrap max-h-36 overflow-y-auto">
                                    {censorBody(stripHtmlTags(detailData.inhaltBereinigt || detailData.originalContent || detailData.inhalt || ''), isEmbed)}
                                  </div>
                                </div>

                                {detailData.aiAntwort && (
                                  <div>
                                    <p className="text-xs text-text-muted font-semibold mb-1 uppercase">KI-Antwort (Vorschlag)</p>
                                    <div className="p-3 rounded-lg bg-blue-50 text-sm whitespace-pre-wrap max-h-36 overflow-y-auto">
                                      {censorBody(stripHtmlTags(detailData.aiAntwort), isEmbed)}
                                    </div>
                                  </div>
                                )}

                                {detailData.gesendeteAntwort && (
                                  <div>
                                    <p className="text-xs text-text-muted font-semibold mb-1 uppercase">Gesendete Antwort</p>
                                    <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-sm">
                                      <div className="flex flex-wrap gap-3 text-xs text-text-muted mb-2">
                                        <span>An: {censorEmail(detailData.gesendeteAntwort.empfaenger, isEmbed)}</span>
                                        <span>Betreff: {censorSubject(detailData.gesendeteAntwort.betreff, isEmbed)}</span>
                                        <span>{new Date(detailData.gesendeteAntwort.gesendetAm).toLocaleString('de-DE')}</span>
                                      </div>
                                      <div className="whitespace-pre-wrap max-h-36 overflow-y-auto">
                                        {censorBody(stripHtmlTags(detailData.gesendeteAntwort.inhalt), isEmbed)}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        ) : (
                          <p className="text-sm text-text-muted">Details konnten nicht geladen werden.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border">
            <p className="text-sm text-text-muted">
              {total} Einträge gesamt
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setPage(p => Math.max(1, p - 1)); setSelectedItem(null); setDetailData(null); }}
                disabled={page <= 1}
                className="p-2 border border-border rounded-lg disabled:opacity-50 hover:bg-gray-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-text-muted px-2">
                Seite {page} von {totalPages}
              </span>
              <button
                onClick={() => { setPage(p => Math.min(totalPages, p + 1)); setSelectedItem(null); setDetailData(null); }}
                disabled={page >= totalPages}
                className="p-2 border border-border rounded-lg disabled:opacity-50 hover:bg-gray-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
